import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import type { GetAllComponentsResponse, GetBinaryComponentPathsParams } from '../../../../shared/types/routes/binary';

/**
 * Function to fetch binary component paths for a release group.
 * Note that we don't use this hook like other React Query hooks,
 * we use it with `ensureQueryData` which requires that we provide it a fetch function directly.
 * @param {number} releaseGroupId - The release group ID.
 * @param {number} releaseId - The release ID.
 * @param {GetBinaryComponentPathsParams} params - The request query params
 */
export function getReleaseGroupComponentPaths(
  releaseGroupId: number,
  releaseId: number,
  params: GetBinaryComponentPathsParams,
) {
  return get<GetAllComponentsResponse>(
    `/api/binary/release-group/${releaseGroupId}/release/${releaseId}/components/paths`,
    false,
    params,
  );
}

export const getReleaseGroupComponentPathsQueryKey = (
  releaseGroupId: number,
  releaseId: number,
  params: GetBinaryComponentPathsParams,
): QueryKey => ['release-group', releaseGroupId, 'release', releaseId, 'components', 'paths', params];

/**
 * useGetReleaseGroupComponentPaths is a hook that fetches and returns the binary component paths for the given release group and release.
 */
export default function useGetReleaseGroupComponentPaths(
  releaseGroupId: number,
  releaseId: number,
  params: GetBinaryComponentPathsParams,
) {
  return useQuery<GetAllComponentsResponse>({
    queryKey: getReleaseGroupComponentPathsQueryKey(releaseGroupId, releaseId, params),
    queryFn: () => getReleaseGroupComponentPaths(releaseGroupId, releaseId, params),
  });
}
